import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    DataTable,
    getTodayIso,
    isNotCanceled,
    parseDateOnly,
    type DataTableMode
} from '@odo/shared-ui';
import { useSearch } from '../../contexts/SearchContext';
import { useRecentActions } from '../../contexts/RecentActionsContext';
import { useAppointmentsMine } from '../../hooks/useAppointments';
import { usePatientList } from '../../providers/patient/PatientsListProvider';
import { usePractice } from '../../providers/practices/PracticesProvider';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import type { Appointment } from '../../services/appointments';
import { hasActionRequired, isInactiveStatus, isOnHold } from '../../utils/constants';
import Loading from '../Loading';
import ConversationPanel from '../messages/ConversationPanel';
import { INACTIVE_CARD_LABEL } from './StatsBar';
import { buildPatientColumns } from './patientColumns';
import { PatientTableCardHeader } from './PatientTableCardHeader';
import {
    isInactivePatient,
    sortPatients,
    type PatientsSortBy,
    type SortDir
} from './patientTableSort';

// Must match the `rowHeightClassName="h-16"` passed to <DataTable> below -
// the header row is excluded automatically, since DataTable's scroll region
// (fed via `refs.scroll`) structurally sits below the header and above the
// footer.
const ROW_HEIGHT = 64;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 50;

// Chunk revealed per scroll/"Load more" step in scroll mode - matches
// shared-ui DataTable's own infinite-mode default chunk size.
const SCROLL_CHUNK_SIZE = 50;

const VIEW_MODE_KEY = 'patientTableViewMode';

type ViewMode = 'paged' | 'scroll';

const PatientTable: React.FC = () => {
    const [sortType, setSortType] = useState<PatientsSortBy>('actionRequired');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [revealedCount, setRevealedCount] = useState(SCROLL_CHUNK_SIZE);

    const handleSortClick = useCallback((type: PatientsSortBy) => {
        setSortType((prevType) => {
            setSortDir((prevDir) =>
                prevType === type ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'
            );
            return type;
        });
        setPage(1);
        setRevealedCount(SCROLL_CHUNK_SIZE);
    }, []);

    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        return stored === 'paged' ? 'paged' : 'scroll';
    });

    // DataTable's `mode` is 'paged' | 'infinite' - the work queue's semantics
    // for the same segmented toggle (see DataTableModeToggle's docstring).
    // This table's "scroll" windows `filteredPatients` into `revealedCount`
    // rows and wires `onLoadMore`/`hasMore`, same shape as InvoicesSection.
    const handleDataTableModeChange = useCallback((mode: DataTableMode) => {
        const next: ViewMode = mode === 'paged' ? 'paged' : 'scroll';
        setViewMode(next);
        localStorage.setItem(VIEW_MODE_KEY, next);
        setRevealedCount(SCROLL_CHUNK_SIZE);
        if (next === 'paged') {
            setPage(1);
        }
    }, []);

    const { patients, isLoading, isFetching, error, refresh, hasMixedPatientVersions } =
        usePatientList();
    const { isFetching: isMetadataFetching, hasUnreadMessages } = usePatientMetadata();
    const { recentActions } = useRecentActions();
    const {
        searchTerm,
        treatmentStatusFilter,
        actionRequiredFilter,
        practiceFilter,
        activeStatFilter
    } = useSearch();
    const { practices } = usePractice();
    const hasMultiplePractices = practices.length > 1;
    const { appointments, isLoading: appointmentsLoading } = useAppointmentsMine();

    // Soonest non-canceled, non-past appointment per patient, keyed by patientId —
    // used to render the "Next appointment" column without an extra fetch.
    const nextAppointmentByPatientId = useMemo(() => {
        const todayIso = getTodayIso();
        const map = new Map<string, Appointment>();
        appointments.forEach((appointment) => {
            if (!isNotCanceled(appointment) || appointment.date < todayIso) return;
            const existing = map.get(appointment.patientId);
            const isEarlier =
                !existing ||
                appointment.date < existing.date ||
                (appointment.date === existing.date &&
                    (appointment.time ?? '') < (existing.time ?? ''));
            if (isEarlier) {
                map.set(appointment.patientId, appointment);
            }
        });
        return map;
    }, [appointments]);

    // Same source as the column's display value (nextAppointmentByPatientId,
    // and transitively the appointments query), reduced to a numeric
    // timestamp so sortPatients compares real date values instead of display
    // strings. Empty while appointments haven't loaded yet, which
    // sortPatients already treats as "no appointment" - the sort settles once
    // the query resolves and this recomputes.
    const nextAppointmentTimestampByPatientId = useMemo(() => {
        const map = new Map<string, number>();
        nextAppointmentByPatientId.forEach((appointment, patientId) => {
            map.set(patientId, parseDateOnly(appointment.date).getTime());
        });
        return map;
    }, [nextAppointmentByPatientId]);

    const [page, setPage] = useState(1);

    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);
    const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);

    const computePageSize = useCallback(() => {
        if (!scrollRef.current) return;
        const usableHeight = scrollRef.current.clientHeight;
        const computed = Math.floor(usableHeight / ROW_HEIGHT);
        setPageSize(Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, computed)));
    }, []);

    useLayoutEffect(() => {
        function handleResize() {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setAvailableHeight(window.innerHeight - top);
            }
            computePageSize();
        }

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isLoading, computePageSize]);

    // Recompute page size once availableHeight has been applied to the layout
    useLayoutEffect(() => {
        computePageSize();
    }, [availableHeight, computePageSize]);

    // Map practice names to IDs for filtering
    const practiceNameToIds = useMemo(() => {
        const map: Record<string, string[]> = {};
        practices.forEach((p) => {
            const name = p.name;
            const id = p.id;
            if (name && id) {
                if (!map[name]) map[name] = [];
                map[name].push(id);
            }
        });
        return map;
    }, [practices]);

    // Filtering: by name/email, status/action, and practice
    const filteredPatients = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        // Only the default view re-admits inactive patients. With a specific stat
        // card selected, suppressing the hatch removes the one mismatch that read
        // as a bug rather than as filtering ("Action required 3" above four rows).
        //
        // This is not a general guarantee that a card's count equals its rows:
        // StatsBar counts the unfiltered roster, so search, practice, status and
        // action filters all still narrow the rows beneath an unchanged count.
        const showsInactive =
            !activeStatFilter &&
            (term.length > 0 || treatmentStatusFilter.some((s) => isInactiveStatus(s)));
        const filtered = patients.filter((p) => {
            const name = (p.name as string | null) || '';
            const email = (p.email as string | null) || '';
            const status = (p.treatmentStatus as string | null) || '';
            const action = (p.requiredAction as string | null) || '';
            const patientPracticeIds = p.practiceIds as string[] | null;

            const matchesSearch =
                !term || name.toLowerCase().includes(term) || email.toLowerCase().includes(term);
            const matchesStatus =
                treatmentStatusFilter.length === 0 || treatmentStatusFilter.includes(status);
            const matchesAction =
                actionRequiredFilter.length === 0 ||
                actionRequiredFilter.includes(action) ||
                (!hasActionRequired(action) && actionRequiredFilter.includes('No action needed'));

            // Practice filter: check if any of the patient's practice IDs match any of the selected practice names
            const matchesPractice =
                practiceFilter.length === 0 ||
                (patientPracticeIds &&
                    patientPracticeIds.some((pid) =>
                        practiceFilter.some((practiceName) =>
                            practiceNameToIds[practiceName]?.includes(String(pid))
                        )
                    ));

            // Inactive patients (complete/declined/archived - the grayed-out
            // rows) are off the default roster so the table only shows live
            // work. They come back when they're what was asked for: the
            // "Inactive" card, an inactive status picked in the status
            // filter, or a search term that might be aimed at one of them.
            const matchesActiveScope =
                activeStatFilter === 'inactive'
                    ? isInactiveStatus(status)
                    : !isInactiveStatus(status) || showsInactive;

            const matchesStatFilter = (() => {
                if (!activeStatFilter) return true;
                switch (activeStatFilter) {
                    case 'action_required':
                        return hasActionRequired(action);
                    case 'unread_messages':
                        return hasUnreadMessages(p.id);
                    case 'on_hold':
                        return isOnHold(status);
                    // 'inactive' needs no clause: matchesActiveScope above already
                    // restricted the rows to inactive patients for that card.
                    case 'inactive':
                        return true;
                    default: {
                        // Exhaustive: a stat filter added to the union without a
                        // clause here is a compile error, not a card that
                        // silently shows every row.
                        const exhaustive: never = activeStatFilter;
                        return exhaustive;
                    }
                }
            })();

            return (
                matchesSearch &&
                matchesStatus &&
                matchesAction &&
                matchesPractice &&
                matchesActiveScope &&
                matchesStatFilter
            );
        });
        filtered.sort((a, b) =>
            sortPatients(
                a,
                b,
                sortType,
                sortDir,
                recentActions,
                nextAppointmentTimestampByPatientId
            )
        );
        return filtered;
    }, [
        patients,
        searchTerm,
        treatmentStatusFilter,
        actionRequiredFilter,
        practiceFilter,
        practiceNameToIds,
        activeStatFilter,
        hasUnreadMessages,
        sortType,
        sortDir,
        recentActions,
        nextAppointmentTimestampByPatientId
    ]);

    // Pagination logic with filtered items
    const paginatedItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredPatients.slice(start, start + pageSize);
    }, [filteredPatients, page, pageSize]);

    // Scroll mode windows the filtered array instead of mounting it all -
    // the same shape InvoicesSection.tsx uses for its own infinite mode.
    const revealedItems = useMemo(
        () => filteredPatients.slice(0, revealedCount),
        [filteredPatients, revealedCount]
    );
    const hasMorePatients = revealedCount < filteredPatients.length;
    const handleLoadMorePatients = useCallback(() => {
        setRevealedCount((prev) => Math.min(prev + SCROLL_CHUNK_SIZE, filteredPatients.length));
    }, [filteredPatients.length]);

    const displayedItems = viewMode === 'scroll' ? revealedItems : paginatedItems;

    const totalPages = Math.max(1, Math.ceil(filteredPatients.length / pageSize));

    // Reset page and revealed count when filters or search change
    useEffect(() => {
        setPage(1);
        setRevealedCount(SCROLL_CHUNK_SIZE);
    }, [searchTerm, treatmentStatusFilter, actionRequiredFilter, practiceFilter, activeStatFilter]);

    // pageSize is viewport-driven and only feeds paged mode - resetting page
    // here (not revealedCount) keeps scroll mode's reveal progress on resize.
    useEffect(() => {
        setPage(1);
    }, [pageSize]);

    // Chat slide-over state
    const [chatPatientId, setChatPatientId] = useState<string | null>(null);

    const chatPatient = useMemo(() => {
        if (!chatPatientId) return null;
        return patients.find((p) => p.id === chatPatientId) ?? null;
    }, [chatPatientId, patients]);

    const handleChatClick = useCallback((patientId: string) => {
        setChatPatientId(patientId);
    }, []);

    const handleChatClose = useCallback(() => {
        setChatPatientId(null);
    }, []);

    const columns = useMemo(
        () =>
            buildPatientColumns({
                showPractice: hasMultiplePractices,
                onChatClick: handleChatClick,
                nextAppointmentByPatientId,
                appointmentsLoading,
                showVersionBadge: hasMixedPatientVersions
            }),
        [
            hasMultiplePractices,
            handleChatClick,
            nextAppointmentByPatientId,
            appointmentsLoading,
            hasMixedPatientVersions
        ]
    );

    // Inactive patients are hidden by default, so an all-inactive practice would
    // otherwise see the same bare "No patients found." as a practice with none.
    const hiddenInactiveCount = useMemo(
        () => patients.filter((p) => isInactiveStatus(p.treatmentStatus as string | null)).length,
        [patients]
    );

    const hasActiveSearchOrFilters = Boolean(
        searchTerm ||
        treatmentStatusFilter.length > 0 ||
        actionRequiredFilter.length > 0 ||
        practiceFilter.length > 0 ||
        activeStatFilter
    );

    if (isLoading) return <Loading />;

    if (error) {
        return (
            <div className="px-8 py-4 w-full h-full flex items-center justify-center">
                <div className="flex flex-col justify-center items-center text-center p-8">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                            className="w-10 h-10 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        Oops! Unable to load patients
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md">{error}</p>
                    <button
                        onClick={() => refresh()}
                        className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="px-8 py-4 w-full flex flex-col overflow-hidden"
            ref={containerRef}
            style={{ height: availableHeight }}
        >
            <DataTable
                label="Patients"
                columns={columns}
                rows={displayedItems}
                getRowKey={(patient) => patient.id}
                rowClassName={(patient) =>
                    // The fade means "deprioritised relative to the rows around
                    // it". Under the Inactive card every row is inactive, so it
                    // would gray the whole table - carrying no signal while
                    // pushing body text under the AA contrast floor.
                    `hover:bg-gray-50 transition-colors duration-150 text-sm text-gray-700${
                        activeStatFilter !== 'inactive' && isInactivePatient(patient)
                            ? ' opacity-40'
                            : ''
                    }`
                }
                rowHeightClassName="h-16"
                sort={{ by: sortType, order: sortDir, onSort: handleSortClick }}
                mode={viewMode === 'scroll' ? 'infinite' : 'paged'}
                onModeChange={handleDataTableModeChange}
                onLoadMore={handleLoadMorePatients}
                hasMore={hasMorePatients}
                pagination={
                    filteredPatients.length > 0
                        ? { page, totalPages, total: filteredPatients.length, onPage: setPage }
                        : undefined
                }
                cardHeader={
                    <PatientTableCardHeader
                        isSyncing={(isFetching || isMetadataFetching) && !isLoading}
                        hasActiveFilters={hasActiveSearchOrFilters}
                        filteredCount={filteredPatients.length}
                        searchTerm={searchTerm}
                    />
                }
                emptyState={
                    <span>
                        {hasActiveSearchOrFilters
                            ? 'No patients found matching your filters.'
                            : hiddenInactiveCount > 0
                              ? `No active patients. ${hiddenInactiveCount} inactive ${
                                    hiddenInactiveCount === 1 ? 'case is' : 'cases are'
                                } hidden - open the ${INACTIVE_CARD_LABEL} card to see them.`
                              : 'No patients found.'}
                    </span>
                }
                emptyStateCellClassName="px-6 py-8 text-center text-gray-500"
                refs={{ scroll: scrollRef }}
                className="flex-1 min-h-0"
            />

            {chatPatient && (
                <ConversationPanel
                    patient={{
                        id: chatPatient.id ?? '',
                        name: chatPatient.name ?? 'Unknown Patient',
                        treatmentStatus: chatPatient.treatmentStatus ?? null,
                        startDate: chatPatient.startDate ?? null,
                        alignerNumber: chatPatient.alignerNumber ?? null,
                        alignerTotal: chatPatient.alignerTotal ?? null,
                        requiredAction: chatPatient.requiredAction ?? null
                    }}
                    onClose={handleChatClose}
                />
            )}
        </div>
    );
};

export default PatientTable;
